import React, { useState, useMemo } from 'react';
import EmployeeFormModal from './EmployeeFormModal';
import EmployeeDrawer from './EmployeeDrawer';
import TimesheetImportModal from './TimesheetImportModal';
import PositionsModal from './PositionsModal';
import { rateLabel, money, plural } from './tsFormat';
import { formatDate } from '../../utils/formatters';

/* Справочник людей. Таблица, а не карточки: людей 40+, и сравнивают их по
   колонкам — ставка, телефон, сколько получил в прошлом месяце. Группы и их
   порядок — как в табеле, чтобы человек искался там же, где он в Excel. */

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase();

const STATUS = [
  { key: 'active', label: 'Работают' },
  { key: 'fired', label: 'Уволены' },
  { key: 'all', label: 'Все' },
];

const StaffTab = ({ employees, positions, jobPositions, onJobPositionsChanged, accounts, lastMonth, loading, isAdmin, onChanged, onImported }) => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [formEmployee, setFormEmployee] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  const counts = useMemo(() => ({
    active: employees.filter(e => e.status === 'active').length,
    fired: employees.filter(e => e.status === 'fired').length,
    all: employees.length,
  }), [employees]);

  const order = useMemo(() => {
    const map = {};
    jobPositions.forEach(p => { map[p.name] = p.sort_order ?? 100; });
    return map;
  }, [jobPositions]);

  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = employees
      .filter(e => (status === 'all' || e.status === status) && (!q || e.full_name.toLowerCase().includes(q)))
      .sort((a, b) => ((order[a.position] ?? 100) - (order[b.position] ?? 100))
        || a.position.localeCompare(b.position, 'ru') || a.full_name.localeCompare(b.full_name, 'ru'));
    const out = [];
    list.forEach(e => {
      const last = out[out.length - 1];
      if (!last || last.position !== e.position) out.push({ position: e.position, rows: [] });
      out[out.length - 1].rows.push(e);
    });
    return out;
  }, [employees, status, search, order]);

  const openNew = () => { setFormEmployee(null); setFormOpen(true); };
  const openEdit = (employee) => { setFormEmployee(employee); setFormOpen(true); };
  const lastTotal = (id) => lastMonth?.totals?.[String(id)];
  const shown = sections.reduce((a, s) => a + s.rows.length, 0);

  return (
    <div className="st-tab">
      <div className="ts-toolbar">
        <input
          className="ts-search"
          type="search"
          placeholder="Найти по фамилии"
          aria-label="Найти по фамилии"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="ts-segment" role="tablist" aria-label="Статус">
          {STATUS.map(s => (
            <button key={s.key} type="button" role="tab" aria-selected={status === s.key}
              className={status === s.key ? 'is-on' : ''} onClick={() => setStatus(s.key)}>
              {s.label} <span className="ts-segment-count">{counts[s.key]}</span>
            </button>
          ))}
        </div>
        <div className="ts-toolbar-end">
          <button type="button" className="ui-btn-ghost" onClick={() => setPositionsOpen(true)}>Должности</button>
          <button type="button" className="ui-btn-ghost" onClick={() => setImportOpen(true)}>Загрузить табель из Excel</button>
          <button type="button" className="ui-btn-primary" onClick={openNew}>Добавить сотрудника</button>
        </div>
      </div>

      {notice && (
        <div className={`hr-notice ${notice.type === 'error' ? 'error' : ''}`} role="status">
          <span>{notice.text}</span>
          <button className="hr-notice-close" onClick={() => setNotice(null)} aria-label="Скрыть">×</button>
        </div>
      )}

      {loading ? (
        <div className="ts-empty-block">Загружаю справочник…</div>
      ) : shown === 0 ? (
        <div className="ts-empty-block">
          <strong>{employees.length === 0 ? 'Справочник пуст' : 'Никого не нашлось'}</strong>
          <span>
            {employees.length === 0
              ? 'Загрузите Excel-табель — люди, должности и ставки подтянутся из него. Или добавьте сотрудника вручную.'
              : 'Проверьте написание фамилии или переключите «Работают / Уволены».'}
          </span>
          {employees.length === 0 && (
            <button type="button" className="ui-btn-primary" onClick={() => setImportOpen(true)}>Загрузить табель из Excel</button>
          )}
        </div>
      ) : (
        <div className="st-table-wrap">
          <table className="st-table">
            <thead>
              <tr>
                <th scope="col">Сотрудник</th>
                <th scope="col">Оплата</th>
                <th scope="col">Телефон</th>
                <th scope="col">В штате с</th>
                <th scope="col" className="ts-num-h">
                  {lastMonth ? `За ${lastMonth.label.toLowerCase()}` : 'Прошлый месяц'}
                </th>
              </tr>
            </thead>
            {sections.map(section => (
              <tbody key={section.position}>
                <tr className="st-group">
                  <th scope="rowgroup" colSpan={5}>
                    {section.position}
                    <span className="st-group-count">
                      {section.rows.length} {plural(section.rows.length, 'человек', 'человека', 'человек')}
                    </span>
                  </th>
                </tr>
                {section.rows.map(e => (
                  <tr key={e.id} className={`st-row ${e.status === 'fired' ? 'is-fired' : ''}`}
                    onClick={() => setDrawerId(e.id)}>
                    <th scope="row">
                      <button type="button" className="st-person" onClick={(ev) => { ev.stopPropagation(); setDrawerId(e.id); }}>
                        <span className="st-avatar" aria-hidden="true">{initials(e.full_name)}</span>
                        <span className="st-name">{e.full_name}</span>
                        {e.status === 'fired' && <span className="st-fired">уволен</span>}
                      </button>
                    </th>
                    <td className={e.rate ? '' : 'st-missing'}>{rateLabel(e.pay_type, e.rate)}</td>
                    <td>{e.phone || <span className="st-dash">—</span>}</td>
                    <td>{e.hire_date ? formatDate(e.hire_date) : <span className="st-dash">—</span>}</td>
                    <td className="ts-num">
                      {lastTotal(e.id) !== undefined ? money(lastTotal(e.id)) : <span className="st-dash">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {positionsOpen && (
        <PositionsModal
          positions={jobPositions}
          onClose={() => setPositionsOpen(false)}
          onChanged={(next) => { onJobPositionsChanged(next); onChanged(); }}
        />
      )}

      {formOpen && (
        <EmployeeFormModal
          employee={formEmployee}
          positions={positions}
          jobPositions={jobPositions}
          accounts={accounts}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); onChanged(); }}
        />
      )}

      {drawerId && (
        <EmployeeDrawer
          employeeId={drawerId}
          isAdmin={isAdmin}
          onClose={() => setDrawerId(null)}
          onEdit={(employee) => { setDrawerId(null); openEdit(employee); }}
          onChanged={onChanged}
        />
      )}

      {importOpen && (
        <TimesheetImportModal
          isAdmin={isAdmin}
          onClose={() => setImportOpen(false)}
          onImported={(result) => {
            setImportOpen(false);
            setNotice({
              type: 'ok',
              text: `Загружено месяцев: ${result.months}. Работают ${result.active} чел., новых в справочнике — ${result.created_employees}.`,
            });
            onImported(result);
          }}
        />
      )}
    </div>
  );
};

export default StaffTab;
